/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

const throttle = (fn, time) => {
  let timeout = null;
  return function () {
    if (timeout) {
      return;
    }
    const context = this;
    const args = arguments;
    const later = () => {
      fn.call(context, ...args);
      timeout = null;
    };
    timeout = setTimeout(later, time);
  };
};

class HScrollSlider {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.h-scroll-track');
    this.slides = Array.from(this.track.querySelectorAll('.h-scroll-slide'));
    this.prevBtn = container.querySelector('.h-scroll-nav-prev');
    this.nextBtn = container.querySelector('.h-scroll-nav-next');
    this.indicators = container.querySelector('.h-scroll-indicators');

    this.autoAdvanceTimer = null;
    this.isPointerOnSlider = false;

    const config = JSON.parse(container.dataset.config || '{}');
    this.autoAdvanceDelay = config.autoAdvanceDelay || 0;

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.generateIndicators();
    this.updateButtonStates();
    this.updateIndicators();
    this.updateGradients();

    const resizeObserver = new ResizeObserver(() => {
      this.generateIndicators();
      this.updateIndicators();
    });
    resizeObserver.observe(this.container);
    this.resizeObserver = resizeObserver;

    if (this.autoAdvanceDelay > 0) {
      this.startAutoAdvance();
    }
  }

  getCurrentPage() {
    const slidesInView = this.getSlidesInView();
    const totalPages = Math.ceil(this.slides.length / slidesInView);
    const { scrollLeft, scrollWidth, clientWidth } = this.track;

    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.track).gap) || 0;
    const slideWithGap = slideWidth + gap;

    const currentSlideIndex = Math.round(scrollLeft / slideWithGap);
    const calculatedPage = Math.floor(currentSlideIndex / slidesInView);

    const SCROLL_THRESHOLD = 10;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - SCROLL_THRESHOLD;

    return isAtEnd ? totalPages - 1 : calculatedPage;
  }

  getFirstSlideOfPage(pageIndex) {
    const slidesInView = this.getSlidesInView();
    const totalPages = Math.ceil(this.slides.length / slidesInView);

    if (pageIndex >= totalPages - 1) {
      return Math.max(0, this.slides.length - slidesInView);
    }

    return pageIndex * slidesInView;
  }

  scrollToNext() {
    const currentPage = this.getCurrentPage();
    const totalPages = Math.ceil(this.slides.length / this.getSlidesInView());

    if (currentPage >= totalPages - 1) {
      return;
    }

    const targetSlideIndex = this.getFirstSlideOfPage(currentPage + 1);
    this.scrollToSlide(targetSlideIndex);
  }

  scrollToPrev() {
    const currentPage = this.getCurrentPage();

    if (currentPage <= 0) {
      return;
    }

    const targetSlideIndex = this.getFirstSlideOfPage(currentPage - 1);
    this.scrollToSlide(targetSlideIndex);
  }

  scrollToSlide(index) {
    const slide = this.slides[index];
    if (!slide) {
      return;
    }

    this.track.scrollTo({
      left:     slide.offsetLeft,
      behavior: 'smooth',
    });
  }

  updateButtonStates() {
    const SCROLL_THRESHOLD = 20;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;

    const isAtStart = scrollLeft <= SCROLL_THRESHOLD;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - SCROLL_THRESHOLD;

    if (this.prevBtn) {
      this.prevBtn.disabled = isAtStart;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = isAtEnd;
    }
  }

  updateIndicators() {
    if (!this.indicators) {
      return;
    }

    const slidesInView = this.getSlidesInView();
    const totalPages = Math.ceil(this.slides.length / slidesInView);

    const { scrollLeft, scrollWidth, clientWidth } = this.track;

    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.track).gap) || 0;
    const slideWithGap = slideWidth + gap;

    const currentSlideIndex = Math.round(scrollLeft / slideWithGap);
    const calculatedPage = Math.floor(currentSlideIndex / slidesInView);

    const SCROLL_THRESHOLD = 10;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - SCROLL_THRESHOLD;

    const activePage = isAtEnd
      ? totalPages - 1
      : Math.min(calculatedPage, totalPages - 1);

    const dots = this.indicators.querySelectorAll('.indicator-dot');
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === activePage);
      dot.setAttribute('aria-selected', i === activePage ? 'true' : 'false');
    });
  }

  getSlidesInView() {
    const computed = getComputedStyle(this.container);
    return (
      parseInt(computed.getPropertyValue('--h-scroll-slides-in-view')) || 1
    );
  }

  generateIndicators() {
    if (!this.indicators) {
      return;
    }

    const slidesInView = this.getSlidesInView();
    const totalSlides = this.slides.length;
    const totalPages = Math.ceil(totalSlides / slidesInView);

    if (totalPages <= 1) {
      this.indicators.innerHTML = '';
      this.indicators.style.display = 'none';
      return;
    }

    this.indicators.style.display = '';
    this.indicators.innerHTML = '';

    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement('button');
      dot.className = 'indicator-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute(
        'aria-label',
        `Page ${i + 1}`
      );
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      if (i === 0) {
        dot.classList.add('is-active');
      }

      dot.addEventListener('click', () => {
        this.scrollToSlide(i * slidesInView);
      });

      this.indicators.appendChild(dot);
    }
  }

  handleScroll() {
    this.updateButtonStates();
    this.updateGradients();
    this.updateIndicators();
  }

  updateGradients() {
    const startGradient = this.container.querySelector(
      '.h-scroll-gradient-start'
    );
    const endGradient = this.container.querySelector('.h-scroll-gradient-end');

    if (!startGradient || !endGradient) {
      return;
    }

    const SCROLL_THRESHOLD = 10;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const isAtStart = scrollLeft <= SCROLL_THRESHOLD;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - SCROLL_THRESHOLD;

    startGradient.style.opacity = isAtStart ? '0' : '1';
    endGradient.style.opacity = isAtEnd ? '0' : '1';
  }

  startAutoAdvance() {
    if (this.autoAdvanceDelay <= 0) {
      return;
    }

    this.autoAdvanceTimer = setInterval(() => {
      if (this.isPointerOnSlider) {
        return;
      }

      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;

      if (isAtEnd) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.scrollToNext();
      }
    }, this.autoAdvanceDelay);
  }

  stopAutoAdvance() {
    if (this.autoAdvanceTimer) {
      clearInterval(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }
  }

  resetAutoAdvance() {
    this.stopAutoAdvance();
    this.startAutoAdvance();
  }

  handlePointerEnter() {
    this.isPointerOnSlider = true;
  }

  handlePointerLeave() {
    this.isPointerOnSlider = false;
  }

  handleFocus() {
    this.isPointerOnSlider = true;
  }

  handleBlur(e) {
    if (!this.container.contains(e.relatedTarget)) {
      this.isPointerOnSlider = false;
    }
  }

  handleKeydown(e) {
    if (!this.container.contains(document.activeElement)) {
      return;
    }

    switch (e.key) {
      case 'ArrowLeft':
        this.scrollToPrev();
        e.preventDefault();
        break;

      case 'ArrowRight':
        this.scrollToNext();
        e.preventDefault();
        break;

      case 'Home':
        this.scrollToSlide(0);
        e.preventDefault();
        break;

      case 'End':
        this.scrollToSlide(this.slides.length - 1);
        e.preventDefault();
        break;

      default:
        break;
    }
  }

  handleSlideKeydown(e) {
    if (e.key === 'Tab') {
      setTimeout(() => {
        const focusedSlide = document.activeElement.closest('.h-scroll-slide');
        if (focusedSlide) {
          const index = this.slides.indexOf(focusedSlide);
          if (index !== -1) {
            this.scrollToSlide(index);
          }
        }
      }, 0);
    }
  }

  setupEventListeners() {
    this.prevBtn?.addEventListener('click', () => this.scrollToPrev());
    this.nextBtn?.addEventListener('click', () => this.scrollToNext());

    this.track.addEventListener(
      'scroll',
      throttle(() => this.handleScroll(), 100)
    );

    this.container.addEventListener('pointerenter', () =>
      this.handlePointerEnter()
    );
    this.container.addEventListener('pointerleave', () =>
      this.handlePointerLeave()
    );

    this.container.addEventListener('focusin', () => this.handleFocus());
    this.container.addEventListener('focusout', (e) => this.handleBlur(e));

    this.container.addEventListener('keydown', (e) => this.handleKeydown(e));

    this.slides.forEach((slide) => {
      slide.addEventListener('keydown', (e) => this.handleSlideKeydown(e));
    });
  }
}

const sliders = document.querySelectorAll(
  '.wp-block-improntad-blocks-h-scroll'
);
sliders.forEach((slider) => new HScrollSlider(slider));
